import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { EMPTY, finalize, forkJoin, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { changeSiteCallback, signinLegacyApi, updateIntegrationAPITokenCookie, updateLastSiteIdCookie } from 'projects/lib-shared-common/src/public-api';
import { environment } from 'projects/environments/environment';

import { AuthService } from '../../../auth/auth.service';
import { SvcSignalRService } from '../../../services/signal-r/svc-signal-r.service';
import { SitesService } from './sites.service';

/**
 * What the caller still has to do once the switch itself has gone through.
 *
 * `reload` — the session now points at the new site and the page must be reloaded to pick it up.
 * `reauth` — the site did change, but one of the two sign-ins did not come back, so the user has to
 * authenticate again. Callers show whatever they use to say that, then send the user to `/sign-out`.
 */
export type SiteSwitchOutcome = 'reload' | 'reauth';

/**
 * Changing the signed-in user's active site.
 *
 * Extracted from `SitesComponent`, which is a legacy Material menu: any shell that lets a user pick a
 * site — the shared topbar in `@solvace/zard`, and every app that adopts it — needs the same
 * multi-step flow, and none of them should re-implement it.
 *
 * The flow is what the platform requires, in order:
 *
 * 1. `UserPreference/change-site` records the choice (and refreshes the cached site list);
 * 2. the legacy callback mirrors it into the legacy platform (skipped on localhost, which has none);
 * 3. both sessions are re-established — the legacy API cookie and the revamp refresh token — because
 *    the tokens carry the site;
 * 4. the caller reloads, since every already-loaded screen holds data from the previous site.
 *
 * Deliberately UI-free: it neither opens dialogs nor reloads the page, so it can live in core and be
 * consumed by libraries that must not depend on Material. The outcome tells the caller what to do.
 */
@Injectable({ providedIn: 'root' })
export class SiteSwitchService {
  private readonly _sitesService = inject(SitesService);
  private readonly _authService = inject(AuthService);
  private readonly _httpClient = inject(HttpClient);
  private readonly _signalR = inject(SvcSignalRService);

  private readonly _changing = signal(false);

  /** True while a switch is in flight, so menus can disable themselves instead of queueing switches. */
  readonly changing = this._changing.asReadonly();

  /**
   * Switches the active site. A second call while one is in flight completes without emitting, so a
   * double click cannot start two switches.
   */
  changeSite(siteId: number): Observable<SiteSwitchOutcome> {
    if (this._changing()) {
      return EMPTY;
    }
    this._changing.set(true);
    // Marks this tab as the one that caused the change, so the site hub's broadcast is not announced
    // back to the user who just made it.
    this._signalR.isWhoChanged = true;

    return forkJoin([this._sitesService.update(siteId), environment.isLocalhost ? of(true) : changeSiteCallback(this._httpClient, siteId)]).pipe(
      switchMap(([done]) => {
        if (!done.isSucess) {
          return throwError(() => new Error('Site change was rejected by the server.'));
        }
        return forkJoin({
          legacy: this._signInLegacy(),
          revamp: this._authService.signInUsingRefreshToken(),
        });
      }),
      map(({ legacy, revamp }): SiteSwitchOutcome => (legacy && revamp ? 'reload' : 'reauth')),
      // The legacy platform reads the active site from this cookie on the next page load, so it has
      // to be written before the caller reloads.
      tap(() => updateLastSiteIdCookie(siteId)),
      // `finalize` rather than a `tap` error handler: it also runs when the caller unsubscribes —
      // a shell destroyed mid-switch would otherwise leave the service busy forever.
      finalize(() => this._changing.set(false))
    );
  }

  /** Re-signs the legacy platform and refreshes its API cookie; localhost has no legacy host to call. */
  private _signInLegacy(): Observable<boolean> {
    if (environment.isLocalhost) {
      updateIntegrationAPITokenCookie('EMPTY_VALUE');
      return of(true);
    }
    return from(this._authService.prepareCognitoFromEnvironment()).pipe(
      switchMap((user) => {
        if (!user) {
          return of(false);
        }
        return signinLegacyApi(this._httpClient, user.baseUrl, user.ssoId).pipe(
          map((res) => {
            updateIntegrationAPITokenCookie(res.token);
            return true;
          })
        );
      })
    );
  }
}
